import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  EmojiEvents as TrophyIcon,
} from "@mui/icons-material";
import { getQuizAttempts, QuizAttempt } from "../services/moduleQuizService";

interface QuizAttemptsModalProps {
  open: boolean;
  onClose: () => void;
  moduleId: string | null;
  moduleName: string;
}

const QuizAttemptsModal: React.FC<QuizAttemptsModalProps> = ({
  open,
  onClose,
  moduleId,
  moduleName,
}) => {
  const [attempts, setAttempts] = useState<QuizAttempt[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAttempts = async () => {
      if (!moduleId || !open) return;

      setLoading(true);
      setError(null);

      try {
        const data = await getQuizAttempts(moduleId);
        setAttempts(data);
      } catch (err) {
        console.error("Failed to fetch quiz attempts:", err);
        setError("Failed to load quiz attempts. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchAttempts();
  }, [moduleId, open]);

  const formatDate = (timestamp: number | string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatTime = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const getScoreColor = (score: number) => {
    if (score >= 70) return "success";
    if (score >= 50) return "warning";
    return "error";
  };

  const getBestAttempt = () => {
    if (attempts.length === 0) return null;
    return attempts.reduce((best, current) =>
      current.score > best.score ? current : best
    );
  };

  const bestAttempt = getBestAttempt();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" component="span">
            Quiz Attempts
          </Typography>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            {moduleName}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && attempts.length === 0 && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No quiz attempts found for this module.
            </Typography>
          </Box>
        )}

        {!loading && !error && attempts.length > 0 && (
          <>
            {/* Best Score Summary */}
            {bestAttempt && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 3,
                  bgcolor: "success.50",
                  border: "1px solid",
                  borderColor: "success.200",
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TrophyIcon sx={{ color: "success.main" }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Best Score: {Math.round(bestAttempt.score)}%
                  </Typography>
                  <Chip
                    label={`Attempt #${bestAttempt.attemptNumber}`}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {bestAttempt.correctAnswers} of {bestAttempt.totalQuestions}{" "}
                  correct
                  {" • "}
                  {bestAttempt.earnedPoints} of {bestAttempt.totalPoints} points
                </Typography>
              </Paper>
            )}

            {/* Attempts Table */}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.50" }}>
                    <TableCell sx={{ fontWeight: 600 }}>Attempt</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Score
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Correct
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Points
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Time
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attempts.map((attempt) => (
                    <TableRow
                      key={attempt.submissionId}
                      sx={{
                        bgcolor:
                          attempt.submissionId === bestAttempt?.submissionId
                            ? "success.50"
                            : "inherit",
                      }}
                    >
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          #{attempt.attemptNumber}
                          {attempt.submissionId ===
                            bestAttempt?.submissionId && (
                            <TrophyIcon
                              sx={{ fontSize: 16, color: "success.main" }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(attempt.completedAt)}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${Math.round(attempt.score)}%`}
                          size="small"
                          color={getScoreColor(attempt.score)}
                          variant="filled"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {attempt.correctAnswers}/{attempt.totalQuestions}
                      </TableCell>
                      <TableCell align="center">
                        {attempt.earnedPoints}/{attempt.totalPoints}
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.5,
                          }}
                        >
                          <AccessTimeIcon
                            sx={{ fontSize: 14, color: "text.secondary" }}
                          />
                          {formatTime(attempt.timeSpent)}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {attempt.score >= 70 ? (
                          <CheckCircleIcon
                            sx={{ color: "success.main", fontSize: 20 }}
                          />
                        ) : (
                          <CancelIcon
                            sx={{ color: "error.main", fontSize: 20 }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary */}
            <Box sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Typography variant="body2" color="text.secondary">
                Total Attempts: <strong>{attempts.length}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Passed:{" "}
                <strong>{attempts.filter((a) => a.score >= 70).length}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average Score:{" "}
                <strong>
                  {Math.round(
                    attempts.reduce((sum, a) => sum + a.score, 0) /
                      attempts.length
                  )}
                  %
                </strong>
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuizAttemptsModal;
